import React, { useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Async from 'react-select/async';
import {
  Grid,
  Paper,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { instance } from '../../services/api';
import { alignCenterXY, InputContainer } from './styles';
import { AnyAssetInput } from '../../store/inputs/AnyAssetInput';

import { GlobalsContext } from '../../store/Globals';

interface IDataType {
  tag: string;
  reader: boolean;
  collection?: string;
}

const CAnyAssetInput = observer(({ item }: { item: AnyAssetInput }) => {
  const globals = useContext(GlobalsContext);
  const { label, selectorValue, onChange, disabled, isKey, isEdit } = item;
  const [dataType, setDataType] = useState<IDataType | null>(null);
  const [dataTypes, setDataTypes] = useState<IDataType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDataTypes = async () => {
      const { data } = await instance.get(`api/query/getSchema`);

      const assetMap: IDataType[] = data.map((asset: any) => ({
        tag: asset.tag,
        reader: asset.readers ? globals.checkPermission(asset.readers) : false,
        collection: asset.collection ? asset.collection : asset.tag,
      }));

      setDataTypes(data ? assetMap : []);

      setLoading(false);
    };

    getDataTypes();
  }, [globals]);

  const getValueItem = (itm: any) => ({
    ...itm,
    ...(dataType?.reader && { '@private': true }),
  });

  const handleKeyAsset = async (
    resolve: (value: unknown) => void,
    assetKeys: string[],
    apiSearchResult: any,
  ) => {
    const {
      data: { props },
    } = await instance.post('/api/query/getSchema/', {
      assetType: apiSearchResult[0][assetKeys[0]]['@assetType'],
    });

    const objKey = props.find((itm: { isKey: boolean }) => itm.isKey);

    if (objKey) {
      const loadedOptions = apiSearchResult.map((itm: any) => {
        const labelValue = assetKeys
          .filter((key) => typeof itm[key] !== 'undefined')
          .map(
            (key) =>
              `${
                typeof itm[key][objKey.tag] !== 'undefined'
                  ? itm[key][objKey.tag]
                  : itm[key]
              }`,
          );

        return {
          value: getValueItem(itm),
          label: `${labelValue}`,
        };
      });

      resolve(apiSearchResult ? loadedOptions : []);
    } else {
      const loadedOptions = apiSearchResult.map((itm: any) => ({
        value: getValueItem(itm),
        label: `${assetKeys.map((key) => `${itm[key]['@assetType']} `)}`,
      }));

      resolve(apiSearchResult ? loadedOptions : []);
    }
  };

  const loadOptions = () => {
    return new Promise((resolve) => {
      (async () => {
        try {
          onChange(null);

          if (!dataType) return;

          if (
            globals.assetList.some(
              (itm: { tag: string }) => itm.tag === dataType.tag,
            )
          ) {
            const queryReq = {
              query: { selector: { '@assetType': dataType.tag }, limit: 100 },
              ...(dataType.reader && { collection: dataType.collection }),
              resolve: false,
            };

            const result = await instance.post('/api/query/search', queryReq);

            const { data } = result;

            const assetSchema = await instance.post('/api/query/getSchema/', {
              assetType: dataType.tag,
            });

            const assetKeys: string[] =
              assetSchema.data && assetSchema.data.props
                ? assetSchema.data.props
                    .filter((itm: { isKey: boolean }) => itm.isKey)
                    .map((itm: { tag: string }) => itm.tag)
                : [];

            // Check if the key is an asset
            if (
              assetKeys &&
              assetKeys.length > 0 &&
              data.result &&
              data.result.length > 0 &&
              typeof data.result[0][assetKeys[0]] === 'object'
            ) {
              await handleKeyAsset(resolve, assetKeys, data.result);
            } else {
              const loadedOptions = data.result.map((itm: any) => ({
                value: getValueItem(itm),
                label: `${assetKeys.map((key) => `${itm[key]} `)}`,
              }));

              resolve(data.result ? loadedOptions : []);
            }
          }
        } catch (error: any) {
          toast.error(error.message || 'Fail to obtain asset data');
        }
      })();
    });
  };

  const loadDataTypeOptions = () => {
    return new Promise((resolve) => {
      resolve(
        dataTypes.map((dt) => ({
          label: dt.tag,
          value: { tag: dt.tag, reader: dt.reader },
        })),
      );
    });
  };

  const handleAssetChange = (v: any) => {
    if (v) {
      setDataType({
        tag: v.label as string,
        reader: v.value.reader,
        collection: v.value.collection,
      });
    } else {
      setDataType(null);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Paper style={{ margin: '10px 0' }}>
      <InputContainer>
        <Grid container spacing={3}>
          <Grid item xs={11}>
            <Typography style={{ color: '#777' }}>Assets</Typography>
            <Async
              isClearable
              cacheOptions
              defaultOptions
              onChange={handleAssetChange}
              placeholder="Select the asset"
              loadOptions={loadDataTypeOptions}
              isDisabled={disabled || (isKey && isEdit)}
            />
          </Grid>
          <Grid item xs={11}>
            <Typography style={{ color: '#777' }}>{label}</Typography>
            <Async
              isClearable
              defaultOptions
              value={selectorValue as any}
              loadOptions={loadOptions}
              key={dataType ? dataType.tag : ''}
              isDisabled={disabled || (isKey && isEdit)}
              onChange={(v: any) => {
                if (v) onChange(v);
                else onChange(null);
              }}
              placeholder={
                dataType ? `Select the ${dataType.tag}` : 'No asset selected'
              }
            />
          </Grid>
          <Grid item xs={1} style={{ ...alignCenterXY, paddingBottom: '0' }}>
            {isKey ? <i className="bx bx-key" style={{ fontSize: '18px' }}></i> : null}
          </Grid>
        </Grid>
      </InputContainer>
    </Paper>
  );
});

export default CAnyAssetInput;
